import React, { useCallback, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { LuArrowLeft, LuCircleAlert, LuPencil } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import Badge from '../../components/ui/Badge/Badge';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import ErrorState from '../../components/ui/ErrorState/ErrorState';
import ResponsiveDialog from '../../components/ui/ResponsiveDialog/ResponsiveDialog';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import { useUserVehicles } from '../../hooks/useUserVehicles';
import { vehicleDisplayFor, VEHICLE_DISPLAY_FALLBACK } from '../../utils/vehicleDisplay';
import { changeMyVehiclePlate } from '../../api/userVehiclesApi';
import './VehicleDetailPage.less';

const BILLING_LABEL = {
  PUBLIC_CARD:     { label: 'Pay with card',         tone: 'neutral' },
  DISCOUNTED_CARD: { label: 'Discounted card rate',  tone: 'info' },
  FLEET_INVOICE:   { label: 'Account billed',        tone: 'success' },
  FREE:            { label: 'Free charging',         tone: 'success' },
};

// Shown when the server refuses a plate that is already registered
// (409 PLATE_ALREADY_REGISTERED) without a message of its own.
export const PLATE_ALREADY_REGISTERED_MESSAGE =
  'This licence plate is already registered. If this is your vehicle, please contact support.';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
      timeZone: 'America/Barbados',
    });
  } catch { return '—'; }
}

/**
 * Vehicle detail view.
 *
 * Users edit metadata via a dedicated Edit page and change the licence
 * plate from this page. A plate change applies immediately when the plate
 * is free; a plate already registered is refused with a clear message.
 * There is no review step.
 */
function VehicleDetailPage() {
  const { vehicleId } = useParams();
  const { vehicles, isLoading, error, refresh } = useUserVehicles();
  const vehicle = vehicles.find((v) => v.plateNormalized === vehicleId);

  const location = useLocation();
  const navigate = useNavigate();

  // Plate change dialog state — only rendered when a vehicle is loaded.
  const [plateDialog, setPlateDialog] = useState(false);
  const [plateInput, setPlateInput] = useState('');
  const [plateSubmitting, setPlateSubmitting] = useState(false);
  const [plateError, setPlateError] = useState(null);
  // Local notice (e.g. no-op); a successful change arrives via route state
  // because the page URL follows the vehicle's new plate.
  const [localNotice, setLocalNotice] = useState(null);
  const plateNotice = localNotice || location.state?.plateNotice || null;

  // Trimmed uppercase value sent to the server (which normalises case,
  // spacing and punctuation itself). The input shows the untrimmed
  // uppercase text so a space can be typed between plate groups.
  const normalizedPlate = (plateInput || '').trim().toUpperCase();
  const canSubmitPlate = normalizedPlate.length >= 2 && normalizedPlate.length <= 20;

  const openPlateDialog = () => {
    setPlateDialog(true);
    setPlateError(null);
    setLocalNotice(null);
  };

  // Stable identity: ResponsiveDialog re-runs its focus effect whenever
  // onClose changes, which would pull focus out of the input on every
  // keystroke.
  const closePlateDialog = useCallback(() => {
    if (plateSubmitting) return;
    setPlateDialog(false);
    setPlateInput('');
    setPlateError(null);
  }, [plateSubmitting]);

  const submitPlate = async (evt) => {
    evt.preventDefault();
    if (!vehicle) return;
    // Guard against re-entry — a fast Enter tap or a double-click
    // on the primary button could otherwise fire two requests.
    if (plateSubmitting) return;
    if (!canSubmitPlate) {
      setPlateError('Enter a valid licence plate (2–20 characters).');
      return;
    }
    setPlateError(null);
    setPlateSubmitting(true);
    try {
      const { changed, vehicle: updated } = await changeMyVehiclePlate(vehicle.id, normalizedPlate);
      setPlateDialog(false);
      setPlateInput('');
      if (!changed) {
        setLocalNotice('This is already the plate on this vehicle.');
        return;
      }
      await refresh();
      const newPlate = updated?.licensePlate || normalizedPlate;
      const nextId = updated?.plateNormalized;
      // The detail URL is keyed on the normalised plate, so follow the
      // vehicle to its new address and carry the success notice along.
      if (nextId && nextId !== vehicleId) {
        navigate(`/vehicles/${encodeURIComponent(nextId)}`, {
          replace: true,
          state: { plateNotice: `Licence plate updated to ${newPlate}.` },
        });
      } else {
        setLocalNotice(`Licence plate updated to ${newPlate}.`);
      }
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'PLATE_ALREADY_REGISTERED') {
        setPlateError(err?.response?.data?.message || PLATE_ALREADY_REGISTERED_MESSAGE);
      } else if (code === 'INVALID_PLATE') {
        setPlateError('This licence plate is not a valid format.');
      } else {
        setPlateError(err?.response?.data?.message || 'Could not change the licence plate. Please try again.');
      }
    } finally {
      setPlateSubmitting(false);
    }
  };

  // Desktop keeps the labelled Back + Edit buttons; mobile trims down
  // to an icon-only Edit in the header's top-right (native-app style)
  // with an inline back link rendered above the header.
  const editHref = `/vehicles/${encodeURIComponent(vehicleId)}/edit`;
  const headerAction = vehicle ? (
    <>
      <Button
        as={Link}
        to="/vehicles"
        variant="ghost"
        leadingIcon={<LuArrowLeft />}
        className="gc-vehicle-detail-back--desktop"
      >
        Back
      </Button>
      <Button
        as={Link}
        to={editHref}
        variant="secondary"
        leadingIcon={<LuPencil />}
        className="gc-vehicle-detail-edit--desktop"
      >
        Edit
      </Button>
      <Link
        to={editHref}
        className="gc-vehicle-detail-edit-icon"
        aria-label="Edit vehicle"
        title="Edit vehicle"
      >
        <LuPencil aria-hidden="true" />
      </Link>
    </>
  ) : (
    <Button as={Link} to="/vehicles" variant="ghost" leadingIcon={<LuArrowLeft />}>
      Back
    </Button>
  );

  const mobileBackLink = vehicle && (
    <Link to="/vehicles" className="gc-vehicle-detail-back-inline">
      <LuArrowLeft aria-hidden="true" />
      <span>Back</span>
    </Link>
  );

  if (isLoading && !vehicle) {
    return (
      <Page>
        <PageHeader title="Vehicle details" action={headerAction}
        className="gc-page-header--compact-actions" />
        <SkeletonCard />
      </Page>
    );
  }

  if (error && !vehicle) {
    return (
      <Page>
        <PageHeader title="Vehicle details" action={headerAction}
        className="gc-page-header--compact-actions" />
        <ErrorState
          title="Could not load this vehicle"
          description={error}
          onRetry={refresh}
        />
      </Page>
    );
  }

  if (!vehicle) {
    return (
      <Page>
        <PageHeader title="Vehicle not found" action={headerAction}
        className="gc-page-header--compact-actions" />
        <Card>
          <EmptyState
            icon={<LuCircleAlert />}
            title="We couldn't find this vehicle"
            description="It may no longer be on your account. If that's a surprise, contact support so we can check the registry."
            action={<Button as={Link} to="/vehicles">Return to vehicles</Button>}
          />
        </Card>
      </Page>
    );
  }

  const billing = vehicle.billingMode && BILLING_LABEL[vehicle.billingMode];
  const displayPlate = vehicle.licensePlate || vehicle.plateNormalized;
  // Canonical vehicle-identity string (make + model + year), preferring
  // the server-computed `displayVehicle` when the DTO carries it.
  // See src/utils/vehicleDisplay.js for the shared rules.
  const canonical = vehicleDisplayFor(vehicle);
  const canonicalIsFallback = canonical === VEHICLE_DISPLAY_FALLBACK;

  return (
    <Page>
      {mobileBackLink}
      <PageHeader
        title={displayPlate}
        // Subtitle prefers the actual vehicle identity — falling back
        // to the nickname only when make/model are missing. The
        // page's Vehicle card below still shows both fields with
        // explicit labels, so nickname isn't hidden.
        subtitle={
          canonicalIsFallback
            ? (vehicle.displayName || 'Vehicle details')
            : canonical
        }
        action={headerAction}
        className="gc-page-header--compact-actions"
      />

      <Card>
        <div className="gc-vehicle-summary">
          <div>
            <p className="gc-vehicle-summary-label">License plate</p>
            <p className="gc-vehicle-summary-plate">{displayPlate}</p>
          </div>
          {billing && (
            <div>
              <p className="gc-vehicle-summary-label">Charging mode</p>
              <Badge tone={billing.tone}>{billing.label}</Badge>
            </div>
          )}
          {vehicle.status === 'inactive' && <Badge tone="warning">Inactive</Badge>}
        </div>
      </Card>

      {(vehicle.make || vehicle.model || vehicle.year || vehicle.color || vehicle.displayName) && (
        <Card>
          <h3 className="gc-vehicle-section-title">Vehicle</h3>
          <dl className="gc-vehicle-details">
            {vehicle.displayName && (<><dt>Name</dt><dd>{vehicle.displayName}</dd></>)}
            {vehicle.make && (<><dt>Make</dt><dd>{vehicle.make}</dd></>)}
            {vehicle.model && (<><dt>Model</dt><dd>{vehicle.model}</dd></>)}
            {vehicle.year != null && vehicle.year !== '' && (
              <><dt>Year</dt><dd>{String(vehicle.year)}</dd></>
            )}
            {vehicle.color && (<><dt>Colour</dt><dd>{vehicle.color}</dd></>)}
          </dl>
        </Card>
      )}

      {vehicle.createdAt && (
        <Card>
          <h3 className="gc-vehicle-section-title">Registration</h3>
          <dl className="gc-vehicle-details">
            <dt>Added</dt>
            <dd>{formatDate(vehicle.createdAt)}</dd>
          </dl>
        </Card>
      )}

      {/* Licence-plate change — applied immediately, no review step. */}
      <Card>
        <h3 className="gc-vehicle-section-title">Licence plate</h3>
        {plateNotice && (
          <p className="gc-plate-change-notice" role="status">{plateNotice}</p>
        )}
        <Button variant="secondary" onClick={openPlateDialog}>
          Change licence plate
        </Button>
      </Card>

      <ResponsiveDialog
        open={plateDialog}
        onClose={closePlateDialog}
        size="sm"
        className="gc-plate-modal"
        title="Change licence plate"
        description="Enter the new plate. The change applies straight away."
        dismissable={!plateSubmitting}
      >
        <form onSubmit={submitPlate} className="gc-plate-modal-form" noValidate>
          <label className="gc-plate-modal-field" htmlFor="gc-plate-modal-input">
            <span className="gc-plate-modal-label">New licence plate</span>
            <input
              id="gc-plate-modal-input"
              className="gc-plate-modal-input"
              type="text"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              value={(plateInput || '').toUpperCase()}
              onChange={(e) => setPlateInput(e.target.value)}
              placeholder="Enter new licence plate"
              maxLength={20}
              disabled={plateSubmitting}
              aria-invalid={plateError ? 'true' : undefined}
              aria-describedby={plateError ? 'gc-plate-modal-error' : undefined}
            />
          </label>
          {plateError && (
            <p id="gc-plate-modal-error" className="gc-plate-modal-error" role="alert">
              {plateError}
            </p>
          )}
          <div className="gc-plate-modal-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={closePlateDialog}
              disabled={plateSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              busy={plateSubmitting}
              disabled={!canSubmitPlate || plateSubmitting}
            >
              {plateSubmitting ? 'Saving…' : 'Save plate'}
            </Button>
          </div>
        </form>
      </ResponsiveDialog>
    </Page>
  );
}

export default VehicleDetailPage;
